import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Compass, UtensilsCrossed, MessageCircle, Map, Landmark, ShoppingBag, LifeBuoy } from "lucide-react";
import { api } from "@/lib/api";
import { heroText } from "@/lib/pageHero";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { PageContentBlocks } from "@/components/site/PageContentBlocks";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { SectionShell } from "@/components/site/edit/SectionShell";

const pick = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";

export default function TouristDeskPage() {
  const { t, lang } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data = { intro: {}, url: "", button: {} } } = useQuery({
    queryKey: ["tourist-desk"],
    queryFn: async () => (await api.get("/tourist-desk")).data,
  });
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const ht = heroText(settings, "tourist-desk", lang, t, "tourist");
  const btnLabel = pick(data.button, lang) || t("tourist.title");

  const features = [
    { Icon: UtensilsCrossed, key: "tourist.f1" },
    { Icon: MessageCircle, key: "tourist.f2" },
    { Icon: Map, key: "tourist.f3" },
    { Icon: Landmark, key: "tourist.f4" },
    { Icon: ShoppingBag, key: "tourist.f5" },
    { Icon: LifeBuoy, key: "tourist.f6" },
  ];

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="tourist-desk-page">
      <div className="grain" />
      <Navbar />

      <PageHero image={data.header_image} eyebrow={ht("eyebrow")} title={ht("title")} description={ht("desc")} icon={Compass} testid="tourist-hero" />

      <PageContentBlocks page="tourist-desk" position="top" />

      <SectionShell id="tourist-desk-sec-1" label="Section 1" noReorder>
      <section className="pt-14 sm:pt-16 pb-24 sm:pb-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="text-lg text-[#475569] leading-relaxed whitespace-pre-line">{pick(data.intro, lang)}</p>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-10" data-testid="tourist-features">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#BE923C] mb-4"><EditableText k="tourist.provide" as="span" /></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map(({ Icon, key }) => (
                  <div key={key} data-testid={`tourist-feature-${key.split(".")[1]}`}
                    className="flex items-center gap-3 rounded-2xl bg-white border border-[#0F172A]/8 px-4 py-3.5 shadow-[0_6px_20px_rgba(15,23,42,0.04)]">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#BE923C]/12 text-[#BE923C]"><Icon size={18} /></span>
                    <span className="text-sm font-medium text-[#0F172A]">{t(key)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {data.url ? (
            <Reveal delay={0.15}>
              <a
                data-testid="tourist-desk-link"
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-8 py-4 shadow-[0_10px_30px_rgba(190,146,60,0.35)] hover:-translate-y-0.5 transition-all"
              >
                {btnLabel} <ArrowUpRight size={17} />
              </a>
            </Reveal>
          ) : (
            <p className="mt-9 text-sm text-[#94A3B8]" data-testid="tourist-desk-nolink"><EditableText k="tourist.nolink" as="span" /></p>
          )}
        </div>
      </section>
      </SectionShell>

      <PageContentBlocks page="tourist-desk" position="bottom" />

      <Footer />
    </div>
  );
}
